import { useEffect, useState } from "react";
import { Plus, X, ArrowUp, ArrowDown, Image as ImageIcon, Type, Heading1, MousePointerClick, Minus, Send } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useSiteSettings } from "@/context/SiteSettingsContext";

const esc = (s = "") => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

const LOGO_ABS = "https://customer-assets-4nw71qhi.emergentagent.net/job_premium-modal/artifacts/sgeh2mfi_c436a83b-25ea-4b6c-9e34-2783c91d017e.png";

export const renderCampaignHtml = ({ blocks, colors, showLogo, logoUrl }) => {
  const body = blocks
    .map((b) => {
      if (b.type === "image" && b.url)
        return `<img src="${b.url}" alt="" style="display:block;width:100%;border-radius:6px;margin:14px 0;" />`;
      if (b.type === "heading")
        return `<h2 style="font-family:Georgia,serif;font-weight:normal;font-size:24px;color:${colors.text};margin:18px 0 8px;">${esc(b.text)}</h2>`;
      if (b.type === "text")
        return esc(b.text)
          .split("\n")
          .filter((l) => l.trim() !== "")
          .map((l) => `<p style="font-size:15px;line-height:1.7;color:${colors.text};margin:0 0 12px;">${l}</p>`)
          .join("");
      if (b.type === "button" && b.label)
        return `<table role="presentation" cellpadding="0" cellspacing="0" border="0" align="center" style="margin:22px auto;"><tr><td bgcolor="${colors.accent}" style="background-color:${colors.accent};border-radius:999px;"><a href="${b.url || "#"}" style="display:inline-block;color:${colors.bg};text-decoration:none;font-size:12px;letter-spacing:2px;text-transform:uppercase;padding:14px 34px;">${esc(b.label)}</a></td></tr></table>`;
      if (b.type === "divider")
        return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="margin:22px 0;"><tr><td bgcolor="#ffffff" style="background-color:#ffffff;height:1px;line-height:1px;font-size:1px;">&nbsp;</td></tr></table>`;
      return "";
    })
    .join("");

  const logoRow = showLogo
    ? `<tr><td align="center" style="padding:34px 0 12px;"><img src="${logoUrl || LOGO_ABS}" alt="LIRA" width="150" style="height:auto;width:150px;display:block;" /></td></tr>
       <tr><td style="padding:0 32px;"><table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0"><tr><td bgcolor="#ffffff" style="background-color:#ffffff;height:1px;line-height:1px;font-size:1px;">&nbsp;</td></tr></table></td></tr>`
    : "";

  return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" bgcolor="${colors.bg}" style="background-color:${colors.bg};margin:0;padding:24px 0;">
    <tr><td align="center">
      <table role="presentation" width="600" cellpadding="0" cellspacing="0" border="0" bgcolor="${colors.bg}" style="background-color:${colors.bg};max-width:600px;width:100%;font-family:Arial,Helvetica,sans-serif;">
        ${logoRow}
        <tr><td style="padding:34px 32px;">${body}</td></tr>
        <tr><td style="padding:0 32px;"><table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0"><tr><td bgcolor="#ffffff" style="background-color:#ffffff;height:1px;line-height:1px;font-size:1px;">&nbsp;</td></tr></table></td></tr>
        <tr><td align="center" style="padding:22px 24px 8px;"><span style="font-size:11px;color:#ffffff;opacity:.85;">You're receiving this because you subscribed or created an account at lirascarves.com</span></td></tr>
        <tr><td align="center" style="padding:0 24px 24px;">
          <p style="font-size:11px;color:#ffffff;margin:0 0 6px;">Don't want to receive emails?</p>
          <a href="https://lirascarves.com/unsubscribe?email=%%UNSUBSCRIBE_EMAIL%%" style="font-size:11px;color:#ffffff;text-decoration:underline;">Unsubscribe</a>
        </td></tr>
      </table>
    </td></tr>
  </table>`;
};

const BLOCK_TYPES = [
  { type: "heading", label: "Heading", icon: Heading1, init: { text: "Your headline here" } },
  { type: "text", label: "Text", icon: Type, init: { text: "Write your message here. Press Enter for a new paragraph." } },
  { type: "image", label: "Image", icon: ImageIcon, init: { url: "" } },
  { type: "button", label: "Button", icon: MousePointerClick, init: { label: "Shop Now", url: "https://lirascarves.com" } },
  { type: "divider", label: "Divider", icon: Minus, init: {} },
];

const inputCls = "w-full border border-[#E5E5E5] rounded-md px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-black";

export const EmailCampaignTab = () => {
  const { logo } = useSiteSettings();
  const [subject, setSubject] = useState("New from LIRA");
  const [audience, setAudience] = useState("both");
  const [customEmails, setCustomEmails] = useState("");
  const [showLogo, setShowLogo] = useState(true);
  const [colors, setColors] = useState({ bg: "#560216", text: "#ffffff", accent: "#ffffff" });
  const [blocks, setBlocks] = useState([
    { id: 1, type: "heading", text: "A little something new" },
    { id: 2, type: "text", text: "Hello,\n\nWe're excited to share our latest arrivals with you." },
    { id: 3, type: "button", label: "Shop the Collection", url: "https://lirascarves.com" },
  ]);
  const [counts, setCounts] = useState(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/admin/audience").then((r) => setCounts(r.data)).catch(() => {});
  }, []);

  const addBlock = (t) => setBlocks((prev) => [...prev, { id: Date.now(), type: t.type, ...t.init }]);
  const updateBlock = (id, patch) => setBlocks((prev) => prev.map((b) => (b.id === id ? { ...b, ...patch } : b)));
  const removeBlock = (id) => setBlocks((prev) => prev.filter((b) => b.id !== id));
  const move = (i, dir) => setBlocks((prev) => {
    const n = [...prev]; const j = i + dir;
    if (j < 0 || j >= n.length) return prev;
    [n[i], n[j]] = [n[j], n[i]]; return n;
  });

  const uploadBlockImage = async (id, file) => {
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      updateBlock(id, { url: r.data.url });
    } catch { toast.error("Image upload failed."); }
  };

  const html = renderCampaignHtml({ blocks, colors, showLogo, logoUrl: LOGO_ABS });

  const parsedCustom = customEmails
    .split(/[\s,;]+/)
    .map((e) => e.trim().toLowerCase())
    .filter((e) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e));
  const uniqueCustom = [...new Set(parsedCustom)];

  const audienceCount = audience === "custom"
    ? uniqueCustom.length
    : (counts ? (audience === "subscribers" ? counts.subscribers : audience === "accounts" ? counts.accounts : counts.total_unique) : null);

  const send = async () => {
    if (!subject.trim()) { toast.error("Please add a subject line."); return; }
    if (audience === "custom" && uniqueCustom.length === 0) { toast.error("Enter at least one valid email address."); return; }
    if (!window.confirm(`Send this email to ${audienceCount ?? "all"} recipient(s)? This cannot be undone.`)) return;
    setSending(true);
    try {
      const r = await api.post("/admin/campaigns/send", { subject, html, audience, custom_emails: audience === "custom" ? uniqueCustom : [] });
      toast.success(`Email is being sent to ${r.data.recipients} recipient(s).`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send campaign.");
    } finally { setSending(false); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_480px] gap-8" data-testid="email-campaign-tab">
      {/* Builder */}
      <div className="space-y-7">
        <div>
          <label className="eyebrow block mb-2">Subject line</label>
          <input value={subject} onChange={(e) => setSubject(e.target.value)} className={inputCls} data-testid="campaign-subject" />
        </div>

        <div className="grid grid-cols-3 gap-4">
          {[["bg", "Background"], ["text", "Text"], ["accent", "Accent / Buttons"]].map(([k, lbl]) => (
            <div key={k}>
              <label className="text-xs text-[#737373] mb-1 block">{lbl}</label>
              <div className="flex items-center gap-2">
                <input type="color" value={colors[k]} onChange={(e) => setColors((c) => ({ ...c, [k]: e.target.value }))} className="h-9 w-10 rounded border border-[#E5E5E5] cursor-pointer" data-testid={`color-${k}`} />
                <span className="text-xs text-[#737373]">{colors[k]}</span>
              </div>
            </div>
          ))}
        </div>

        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={showLogo} onChange={(e) => setShowLogo(e.target.checked)} data-testid="toggle-logo" /> Show LIRA logo at top
        </label>

        {/* Blocks */}
        <div>
          <label className="eyebrow block mb-3">Content blocks</label>
          <div className="space-y-3">
            {blocks.map((b, i) => (
              <div key={b.id} className="border border-[#E5E5E5] rounded-md p-3" data-testid={`block-${b.type}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs uppercase tracking-[0.15em] text-[#737373]">{b.type}</span>
                  <div className="flex items-center gap-1">
                    <button onClick={() => move(i, -1)} className="p-1 text-[#737373] hover:text-black"><ArrowUp className="h-4 w-4" /></button>
                    <button onClick={() => move(i, 1)} className="p-1 text-[#737373] hover:text-black"><ArrowDown className="h-4 w-4" /></button>
                    <button onClick={() => removeBlock(b.id)} className="p-1 text-[#737373] hover:text-[#9a3b3b]"><X className="h-4 w-4" /></button>
                  </div>
                </div>
                {b.type === "heading" && (
                  <input value={b.text} onChange={(e) => updateBlock(b.id, { text: e.target.value })} className={inputCls} placeholder="Heading text" />
                )}
                {b.type === "text" && (
                  <textarea rows={4} value={b.text} onChange={(e) => updateBlock(b.id, { text: e.target.value })} className={inputCls} placeholder="Body text (Enter for new paragraph)" />
                )}
                {b.type === "image" && (
                  <div className="space-y-2">
                    {b.url && <img src={b.url} alt="" className="h-24 rounded object-cover" />}
                    <div className="flex items-center gap-2">
                      <input value={b.url} onChange={(e) => updateBlock(b.id, { url: e.target.value })} className={inputCls} placeholder="Image URL or upload →" />
                      <label className="shrink-0 text-xs uppercase tracking-wider border border-[#E5E5E5] rounded-md px-3 py-2 cursor-pointer hover:border-black">
                        Upload
                        <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && uploadBlockImage(b.id, e.target.files[0])} />
                      </label>
                    </div>
                  </div>
                )}
                {b.type === "button" && (
                  <div className="grid grid-cols-2 gap-2">
                    <input value={b.label} onChange={(e) => updateBlock(b.id, { label: e.target.value })} className={inputCls} placeholder="Button text" />
                    <input value={b.url} onChange={(e) => updateBlock(b.id, { url: e.target.value })} className={inputCls} placeholder="Link URL" />
                  </div>
                )}
                {b.type === "divider" && <p className="text-xs text-[#a8a29a]">A thin horizontal line.</p>}
              </div>
            ))}
          </div>
          <div className="flex flex-wrap gap-2 mt-3">
            {BLOCK_TYPES.map((t) => (
              <button key={t.type} onClick={() => addBlock(t)} className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.12em] border border-[#E5E5E5] rounded-full px-3 py-2 hover:border-black transition-colors" data-testid={`add-block-${t.type}`}>
                <t.icon className="h-3.5 w-3.5" /> {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* Audience + send */}
        <div className="border-t border-[#E5E5E5] pt-6">
          <label className="eyebrow block mb-3">Send to</label>
          <div className="space-y-2 text-sm">
            {[["both", "Everyone (subscribers + account holders)", counts?.total_unique],
              ["subscribers", "Newsletter subscribers", counts?.subscribers],
              ["accounts", "Account holders", counts?.accounts],
              ["custom", "Specific people (enter emails)", null]].map(([val, lbl, c]) => (
              <label key={val} className="flex items-center gap-2 cursor-pointer" data-testid={`audience-${val}`}>
                <input type="radio" name="audience" checked={audience === val} onChange={() => setAudience(val)} />
                {lbl} {c != null && <span className="text-[#737373]">· {c}</span>}
              </label>
            ))}
          </div>
          {audience === "custom" && (
            <div className="mt-3">
              <textarea
                value={customEmails}
                onChange={(e) => setCustomEmails(e.target.value)}
                rows={3}
                placeholder="Enter one or more emails, separated by commas or new lines&#10;e.g. jane@example.com, ali@example.com"
                className={inputCls}
                data-testid="custom-emails-input"
              />
              <p className="text-xs text-[#737373] mt-1" data-testid="custom-emails-count">
                {uniqueCustom.length} valid email{uniqueCustom.length === 1 ? "" : "s"} detected.
              </p>
            </div>
          )}
          <button onClick={send} disabled={sending} className="btn-primary mt-5 inline-flex items-center gap-2 disabled:opacity-50" data-testid="send-campaign-button">
            <Send className="h-4 w-4" /> {sending ? "Sending…" : `Send to ${audienceCount ?? "…"} recipient(s)`}
          </button>
        </div>
      </div>

      {/* Live preview */}
      <div className="lg:sticky lg:top-6 h-fit">
        <label className="eyebrow block mb-3">Live preview</label>
        <div className="border border-[#E5E5E5] rounded-md overflow-hidden">
          <iframe title="email-preview" srcDoc={html} className="w-full h-[640px] bg-white" data-testid="campaign-preview" />
        </div>
      </div>
    </div>
  );
};
